import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChatBubbleLeftRightIcon, PaperClipIcon, PaperAirplaneIcon, ArrowDownTrayIcon, XMarkIcon, MagnifyingGlassIcon, PlusCircleIcon, SignalIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { chatService } from '../services/chatService';
import { websocketService } from '../services/websocketService';
import { guestService } from '../services/guestService';
import { ChatThread, ChatMessage, ChatAttachment, ChatAvailability } from '../types/chat';
import { Guest } from '../types/guest';

// ---------- Thread List (left panel) ----------

interface ThreadListProps {
  threads: ChatThread[];
  loading: boolean;
  selectedId?: string;
  availability: ChatAvailability | null;
  onSelect: (t: ChatThread) => void;
  onNewConversation: () => void;
  statusFilter: string;
  onStatusFilterChange: (s: string) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  onSearch: () => void;
}

function ThreadList({ threads, loading, selectedId, availability, onSelect, onNewConversation, statusFilter, onStatusFilterChange, searchQuery, onSearchChange, onSearch }: ThreadListProps) {
  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      open: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const timeAgo = (ts: string) => {
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(diff / 3600000);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(diff / 86400000);
    return days < 7 ? `${days}d` : new Date(ts).toLocaleDateString();
  };

  const hasAvailability = Boolean(availability?.available_count);

  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-200">
      <div className="p-4 border-b border-gray-200 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Messages</h2>
          <p className="text-sm text-gray-500 mt-1">Correspondants CNS, ONT et ANR — dossiers clients ou demande générale.</p>
        </div>

        <div className={`rounded-lg border px-3 py-3 ${hasAvailability ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
          <div className="flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full ${hasAvailability ? 'bg-green-500' : 'bg-amber-500'}`} />
            <span className={`text-sm font-medium ${hasAvailability ? 'text-green-800' : 'text-amber-800'}`}>
              {hasAvailability ? `${availability?.available_count} contact en ligne` : 'Aucun contact en ligne'}
            </span>
          </div>
          <p className={`mt-1 text-xs ${hasAvailability ? 'text-green-700' : 'text-amber-700'}`}>
            {hasAvailability ? 'Vos messages arrivent en temps réel.' : availability?.unavailable_message || 'Envoyez votre message, il sera conservé pour le prochain agent disponible.'}
          </p>
        </div>

        <button
          onClick={onNewConversation}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          <PlusCircleIcon className="h-5 w-5" />
          Nouvelle conversation
        </button>

        <div className="flex space-x-2">
          <input
            type="text"
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onSearch()}
            placeholder="Rechercher..."
            className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button onClick={onSearch} className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <MagnifyingGlassIcon className="w-4 h-4" />
          </button>
        </div>

        <select
          value={statusFilter}
          onChange={e => onStatusFilterChange(e.target.value)}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Toutes les conversations</option>
          <option value="open">Ouvert</option>
          <option value="in_progress">En cours</option>
          <option value="resolved">Resolu</option>
          <option value="closed">Ferme</option>
        </select>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : threads.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center text-gray-500 p-6">
            <ChatBubbleLeftRightIcon className="w-12 h-12 mb-3 text-gray-400" />
            <p className="text-sm font-medium text-gray-700">Aucune conversation trouvée</p>
            <p className="mt-1 text-xs leading-5">Sélectionnez un client pour démarrer une demande avec ONT/Admin.</p>
            <button onClick={onNewConversation} className="mt-4 text-sm font-medium text-blue-600 hover:text-blue-700">
              Voir les clients
            </button>
          </div>
        ) : (
          threads.map(t => (
            <div
              key={t.id}
              onClick={() => onSelect(t)}
              className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                selectedId === t.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
              } ${t.unread_count_hotel > 0 ? 'bg-red-50' : ''}`}
            >
              <div className="flex justify-between items-start mb-1">
                <span className="font-medium text-gray-800 text-sm truncate flex-1">
                  {t.guest_name || t.hotel_name || (t.id ? `Thread #${t.id.slice(0, 8)}` : 'Conversation')}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full ml-2 ${getStatusBadge(t.status)}`}>
                  {(t.status || 'open').replace('_', ' ')}
                </span>
              </div>

              <div className="text-xs text-blue-700 font-medium mb-0.5">
                {t.recipient_label || recipientLabel(t.recipient_type)}
                {Array.isArray(t.cc_recipient_types) && t.cc_recipient_types.includes('ont_recovery') && (
                  <span className="ml-1 text-amber-700 font-normal">(+ Recouvrement)</span>
                )}
              </div>

              {t.priority === 'urgent' && (
                <span className="inline-block bg-red-500 text-white text-xs px-2 py-0.5 rounded-full mb-1">URGENT</span>
              )}

              <div className="flex justify-between items-center text-xs text-gray-500 mt-1">
                <span>{t.assigned_to ? 'Assigne' : 'Non assigne'}</span>
                <span>{timeAgo(t.last_message_at || t.updated_at)}</span>
              </div>

              {t.last_message_preview && (
                <p className="text-sm text-gray-600 truncate mt-1">{t.last_message_preview}</p>
              )}

              {t.unread_count_hotel > 0 && (
                <span className="inline-flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 mt-1">
                  {t.unread_count_hotel > 9 ? '9+' : t.unread_count_hotel}
                </span>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ---------- Attachment bubble ----------

function AttachmentBubble({ att, messageId }: { att: ChatAttachment; messageId: string }) {
  const [downloading, setDownloading] = useState(false);
  const isImage = /\.(jpe?g|png)$/i.test(att.file_name);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await chatService.downloadAttachment(messageId, att.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.file_name;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  const sizeLabel = att.file_size < 1024 * 1024
    ? `${(att.file_size / 1024).toFixed(0)} KB`
    : `${(att.file_size / (1024 * 1024)).toFixed(1)} MB`;

  return (
    <div className="mt-2 p-2 bg-black bg-opacity-10 rounded flex items-center space-x-2 text-xs">
      {isImage ? (
        <span className="text-lg">🖼</span>
      ) : (
        <PaperClipIcon className="w-4 h-4 flex-shrink-0" />
      )}
      <span className="truncate max-w-[140px]">{att.file_name}</span>
      <span className="opacity-75">({sizeLabel})</span>
      <button
        onClick={handleDownload}
        disabled={downloading}
        className="ml-auto flex-shrink-0 hover:opacity-75"
        title="Telecharger"
      >
        {downloading ? (
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : (
          <ArrowDownTrayIcon className="w-4 h-4" />
        )}
      </button>
    </div>
  );
}

// ---------- Conversation view (right panel) ----------

interface ConversationViewProps {
  thread: ChatThread;
  availability: ChatAvailability | null;
  onThreadUpdate: () => void;
}

function EmptyConversationState({ availability, onNewConversation }: { availability: ChatAvailability | null; onNewConversation: () => void }) {
  const hasAvailability = Boolean(availability?.available_count);

  return (
    <div className="flex h-full items-center justify-center bg-gray-50 px-6">
      <div className="max-w-xl text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-gray-200">
          <ChatBubbleLeftRightIcon className="h-9 w-9 text-blue-600" />
        </div>
        <h2 className="mt-5 text-2xl font-semibold text-gray-900">Messagerie sécurisée</h2>
        <p className="mt-2 text-sm leading-6 text-gray-600">
          Échangez avec ONT/Admin sur un dossier client, envoyez des justificatifs et suivez les réponses en temps réel.
        </p>

        <div className="mt-6 grid gap-3 text-left sm:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
              <SignalIcon className={`h-5 w-5 ${hasAvailability ? 'text-green-600' : 'text-amber-600'}`} />
              {hasAvailability ? `${availability?.available_count} contact en ligne` : 'Message différé disponible'}
            </div>
            <p className="mt-2 text-xs leading-5 text-gray-500">
              {hasAvailability ? 'Les réponses apparaissent automatiquement.' : 'Votre message reste enregistré si aucun agent n’est connecté.'}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
              <UserGroupIcon className="h-5 w-5 text-blue-600" />
              Démarrage par dossier
            </div>
            <p className="mt-2 text-xs leading-5 text-gray-500">
              Ouvrez une fiche client pour lancer une conversation liée au séjour.
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <button onClick={onNewConversation} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
            <PlusCircleIcon className="h-5 w-5" />
            Choisir un client
          </button>
          <span className="text-xs text-gray-500">Formats acceptés: PDF, JPG, PNG, MP4, MOV jusqu’à 10 Mo.</span>
        </div>
      </div>
    </div>
  );
}

function ConversationView({ thread, availability, onThreadUpdate }: ConversationViewProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [attachments, setAttachments] = useState<Record<string, ChatAttachment[]>>({});
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const onThreadUpdateRef = useRef(onThreadUpdate);
  onThreadUpdateRef.current = onThreadUpdate;

  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf', 'video/mp4', 'video/quicktime'];
  const MAX_FILE_SIZE = 10 * 1024 * 1024;
  const MAX_FILES = 5;
  const appendUniqueMessage = useCallback((message: ChatMessage) => {
    setMessages(prev => prev.some(existing => existing.id === message.id) ? prev : [...prev, message]);
  }, []);

  const loadMessages = useCallback(async () => {
    try {
      setLoading(true);
      const msgs = await chatService.getMessages(thread.id);
      setMessages(msgs);
      // Opening a thread marks the other side's messages read. Refresh the
      // list so the unread badge disappears immediately.
      onThreadUpdateRef.current();

      // Load attachments for messages that have them
      const attMap: Record<string, ChatAttachment[]> = {};
      for (const m of msgs) {
        if (m.has_attachments) {
          try {
            const atts = await chatService.getAttachments(m.id);
            if (atts && atts.length > 0) attMap[m.id] = atts;
          } catch { /* ignore */ }
        }
      }
      setAttachments(attMap);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  }, [thread.id]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // WebSocket: payload is not decrypted — reload history from API
  useEffect(() => {
    const handler = (msg: ChatMessage) => {
      if (msg.thread_id === thread.id) {
        loadMessages();
      }
    };
    websocketService.onMessage(handler);
    return () => { websocketService.removeMessageHandler(handler); };
  }, [loadMessages, thread.id]);

  // Poll while conversation is open (fallback if WS drops)
  useEffect(() => {
    const timer = window.setInterval(() => {
      loadMessages();
    }, 8000);
    return () => window.clearInterval(timer);
  }, [loadMessages]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    const valid: File[] = [];
    const errors: string[] = [];

    for (const file of selected) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`${file.name}: type non autorise (JPG, PNG, PDF, MP4, MOV)`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: trop volumineux (max 10 Mo)`);
        continue;
      }
      if (files.length + valid.length >= MAX_FILES) {
        errors.push(`Maximum ${MAX_FILES} fichiers`);
        break;
      }
      valid.push(file);
    }
    if (errors.length) alert(errors.join('\n'));
    if (valid.length) setFiles(prev => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSend = async () => {
    if (!content.trim() && files.length === 0) return;

    setSending(true);
    try {
      const messageContent = content.trim() || 'Fichier joint';
      const message = await chatService.sendMessage(thread.id, messageContent);

      for (const file of files) {
        await chatService.uploadAttachment(message.id, file);
      }

      setContent('');
      setFiles([]);
      await loadMessages();
      onThreadUpdate();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Echec de l\'envoi. Veuillez reessayer.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (ts: string) => new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const formatDate = (ts: string) => new Date(ts).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  const formatFileSize = (b: number) => b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / (1024 * 1024)).toFixed(1)} MB`;

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="bg-blue-600 text-white px-6 py-4 flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">Communication avec les autorites</h2>
          <p className="text-sm text-blue-100">{thread.guest_name || (thread.id ? `Thread #${thread.id.slice(0, 8)}` : 'Conversation')} &middot; {(thread.status || 'open').replace('_', ' ')}</p>
          <p className="text-xs text-blue-100 mt-1">
            {availability && availability.available_count > 0
              ? `${availability.available_count} contact${availability.available_count > 1 ? 's' : ''} ONT/Admin en ligne`
              : availability?.unavailable_message || 'Aucun agent en ligne pour le moment'}
          </p>
        </div>
        {thread.priority === 'urgent' && (
          <span className="bg-red-500 text-white text-xs px-3 py-1 rounded-full font-semibold">URGENT</span>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-3 bg-gray-50">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <ChatBubbleLeftRightIcon className="w-16 h-16 mb-3" />
            <p className="text-sm font-medium text-gray-600">Aucun message dans cette conversation</p>
            <p className="mt-1 text-xs text-gray-500">Décrivez la demande ou joignez un fichier pour prévenir ONT/Admin.</p>
          </div>
        ) : (
          messages.map((msg, i) => {
            const isHotel = msg.sender_type === 'hotel';
            const agencyLabel =
              msg.sender_type === 'ont' ? 'ONT'
                : msg.sender_type === 'anr' ? 'ANR'
                : msg.sender_type === 'admin' || msg.sender_type === 'support' ? 'Support'
                : msg.sender_type === 'cns' ? 'CNS'
                : 'Autorite';
            const showDate = i === 0 || formatDate(messages[i - 1].created_at) !== formatDate(msg.created_at);
            const msgAttachments = attachments[msg.id] || [];

            return (
              <div key={msg.id}>
                {showDate && (
                  <div className="flex justify-center my-3">
                    <span className="bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full">{formatDate(msg.created_at)}</span>
                  </div>
                )}
                <div className={`flex ${isHotel ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[70%] rounded-lg px-4 py-2 shadow ${isHotel ? 'bg-blue-600 text-white' : 'bg-white text-gray-800'}`}>
                    <div className={`text-xs font-semibold mb-1 ${isHotel ? 'text-blue-200' : 'text-gray-500'}`}>
                      {isHotel ? 'Vous' : agencyLabel}
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>

                    {msgAttachments.map(att => (
                      <AttachmentBubble key={att.id} att={att} messageId={msg.id} />
                    ))}

                    <div className={`text-xs mt-1 ${isHotel ? 'text-blue-200' : 'text-gray-400'}`}>
                      {formatTime(msg.created_at)}
                      {msg.delivered_at && ' \u2713'}
                      {msg.read_at && ' \u2713\u2713'}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Composer */}
      {thread.status !== 'closed' ? (
        <div className="border-t bg-white px-4 py-3">
          {/* File preview */}
          {files.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {files.map((f, i) => (
                <div key={i} className="flex items-center bg-gray-100 rounded px-2 py-1 text-xs">
                  <PaperClipIcon className="w-3 h-3 mr-1" />
                  <span className="truncate max-w-[120px]">{f.name}</span>
                  <span className="text-gray-500 ml-1">({formatFileSize(f.size)})</span>
                  <button onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))} className="ml-1 text-red-500 hover:text-red-700">
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-end space-x-2">
            <div className="flex-1">
              <textarea
                value={content}
                onChange={e => setContent(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Tapez votre message..."
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows={2}
                disabled={sending}
              />
              <div className="text-xs text-gray-400 mt-0.5">{content.length}/2000</div>
            </div>

            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || files.length >= MAX_FILES}
              className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50 transition-colors"
              title="Joindre un fichier"
            >
              <PaperClipIcon className="w-5 h-5 text-gray-600" />
            </button>

            <button
              onClick={handleSend}
              disabled={sending || (!content.trim() && files.length === 0)}
              className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 transition-colors"
            >
              {sending ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <PaperAirplaneIcon className="w-5 h-5" />
              )}
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".jpg,.jpeg,.png,.pdf,.mp4,.mov"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      ) : (
        <div className="px-6 py-4 bg-gray-100 text-center text-gray-500 text-sm">
          Cette conversation est fermee
        </div>
      )}
    </div>
  );
}

/** Structured hotel messaging correspondents (kept in sync with GET /api/v1/chat/correspondents) */
const RECIPIENT_OPTIONS = [
  {
    id: 'anr',
    label: 'ANR',
    description: 'Agence Nationale de Renseignements — demandes et signalements institutionnels',
  },
  {
    id: 'cns',
    label: 'CNS',
    description: 'Conseil National de Sécurité — fiches de police, sécurité et conformité hôtelière',
  },
  {
    id: 'ont_admin',
    label: 'ONT — Administration',
    description: "Administration de l'Office National du Tourisme",
  },
  {
    id: 'ont_support',
    label: 'ONT — Support Technique',
    description: "Assistance technique (supervision équipe WAYS)",
  },
  {
    id: 'ont_recovery',
    label: 'ONT — Recouvrement',
    description: 'Service de recouvrement — suivi des dossiers (également en copie automatique de toutes les communications)',
  },
  {
    id: 'ont_secretariat',
    label: 'ONT — Secrétariat Général',
    description: "Secrétariat Général de l'Office National du Tourisme",
  },
];

const RECIPIENT_LABELS: Record<string, string> = Object.fromEntries(
  RECIPIENT_OPTIONS.map((o) => [o.id, o.label]).concat([
    ['ont', 'ONT'],
    ['cns', 'CNS'],
    ['support', 'Support'],
    ['admin', 'Administration'],
  ])
);

function recipientLabel(type?: string): string {
  if (!type) return 'Correspondant';
  return RECIPIENT_LABELS[type] || type;
}

interface CorrespondentPickerProps {
  open: boolean;
  onClose: () => void;
  /** Start general conversation with agency desk (no guest required). */
  onSelectGeneral: (recipientType: string) => void;
  /** Optional guest-linked conversation. */
  onSelectGuest: (guest: Guest, recipientType: string) => void;
}

function CorrespondentPickerModal({ open, onClose, onSelectGeneral, onSelectGuest }: CorrespondentPickerProps) {
  const [search, setSearch] = useState('');
  const [guests, setGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState<string | null>(null);
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null);
  const [mode, setMode] = useState<'general' | 'guest'>('general');
  const [apiCorrespondents, setApiCorrespondents] = useState(RECIPIENT_OPTIONS);

  useEffect(() => {
    if (!open) return;
    setMode('general');
    setSelectedGuest(null);
    setSearch('');
    chatService.listCorrespondents().then((list) => {
      if (list.length > 0) {
        setApiCorrespondents(
          list.map((c) => ({
            id: c.id,
            label: c.label,
            description: c.description || '',
          }))
        );
      }
    }).catch(() => { /* keep static fallback */ });
  }, [open]);

  const searchGuests = useCallback(async (query: string) => {
    setLoading(true);
    try {
      const result = await guestService.getGuests({ search: query }, 1, 20);
      setGuests(result.guests);
    } catch (err) {
      console.error('Error searching guests:', err);
      setGuests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && mode === 'guest') {
      searchGuests('');
    }
  }, [open, mode, searchGuests]);

  const handleSearch = () => {
    searchGuests(search);
  };

  const handleRecipientClick = async (recipientType: string) => {
    setCreating(recipientType);
    try {
      if (selectedGuest) {
        onSelectGuest(selectedGuest, recipientType);
      } else {
        onSelectGeneral(recipientType);
      }
    } finally {
      setCreating(null);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            {selectedGuest && (
              <button onClick={() => setSelectedGuest(null)} className="text-gray-400 hover:text-gray-600 mr-1">
                <span className="text-lg">&larr;</span>
              </button>
            )}
            <h3 className="text-lg font-semibold text-gray-900">
              {selectedGuest ? 'Choisir le destinataire' : 'Nouvelle conversation'}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {!selectedGuest && (
          <div className="px-5 pt-3 flex gap-2 border-b pb-3">
            <button
              type="button"
              onClick={() => setMode('general')}
              className={`flex-1 px-3 py-2 text-sm rounded-lg border ${mode === 'general' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200'}`}
            >
              Correspondant institutionnel
            </button>
            <button
              type="button"
              onClick={() => setMode('guest')}
              className={`flex-1 px-3 py-2 text-sm rounded-lg border ${mode === 'guest' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200'}`}
            >
              Liée à un client
            </button>
          </div>
        )}

        {mode === 'general' && !selectedGuest ? (
          <div className="flex-1 overflow-y-auto p-4">
            <p className="text-sm text-gray-600 mb-2">Choisissez CNS, ONT ou ANR — aucun client requis.</p>
            <p className="text-xs text-amber-700 mb-3 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
              Recouvrement ONT est automatiquement en copie de toutes les communications.
            </p>
            {apiCorrespondents.map(opt => (
              <button
                key={opt.id}
                onClick={() => handleRecipientClick(opt.id)}
                disabled={creating !== null}
                className="w-full text-left px-4 py-3 mb-2 border rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900 text-sm">{opt.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{opt.description}</div>
                  </div>
                  {creating === opt.id ? (
                    <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <ChatBubbleLeftRightIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  )}
                </div>
              </button>
            ))}
          </div>
        ) : !selectedGuest ? (
          <>
            <div className="px-5 py-3 border-b">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  placeholder="Rechercher par nom, passeport, chambre..."
                  className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
                <button onClick={handleSearch} className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  <MagnifyingGlassIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              ) : guests.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm space-y-3">
                  <p>Aucun client trouvé pour lier la conversation.</p>
                  <button
                    type="button"
                    onClick={() => setMode('general')}
                    className="text-blue-600 underline text-sm"
                  >
                    Contacter un correspondant sans client
                  </button>
                </div>
              ) : (
                guests.map(guest => (
                  <button
                    key={guest.id}
                    onClick={() => setSelectedGuest(guest)}
                    className="w-full text-left px-5 py-3 border-b hover:bg-blue-50 transition-colors flex items-center justify-between"
                  >
                    <div>
                      <div className="font-medium text-gray-900 text-sm">
                        {guest.firstName} {guest.lastName}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {guest.roomNumber && `Chambre ${guest.roomNumber}`}
                        {guest.roomNumber && guest.nationality && ' · '}
                        {guest.nationality}
                        {guest.passportNumber && ` · ${guest.passportNumber}`}
                      </div>
                    </div>
                    <PlusCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <div className="px-5 py-3 border-b bg-blue-50">
              <p className="text-sm text-blue-800">
                Client : <strong>{selectedGuest.firstName} {selectedGuest.lastName}</strong>
                {selectedGuest.roomNumber && ` · Chambre ${selectedGuest.roomNumber}`}
              </p>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-2">Choisissez le correspondant :</p>
              <p className="text-xs text-amber-700 mb-3 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
                Recouvrement ONT est automatiquement en copie de toutes les communications.
              </p>
              {apiCorrespondents.map(opt => (
                <button
                  key={opt.id}
                  onClick={() => handleRecipientClick(opt.id)}
                  disabled={creating !== null}
                  className="w-full text-left px-4 py-3 mb-2 border rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900 text-sm">{opt.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{opt.description}</div>
                    </div>
                    {creating === opt.id ? (
                      <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <ChatBubbleLeftRightIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Main page ----------

export default function MessagesPage() {
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [selectedThread, setSelectedThread] = useState<ChatThread | null>(null);
  const [availability, setAvailability] = useState<ChatAvailability | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showGuestPicker, setShowGuestPicker] = useState(false);

  const loadThreads = useCallback(async () => {
    try {
      setLoading(true);
      const filters: Record<string, string> = {};
      if (statusFilter) filters.status = statusFilter;
      if (searchQuery) filters.search = searchQuery;
      const data = await chatService.getThreads(filters);
      setThreads(data);
    } catch (error) {
      console.error('Error loading threads:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery]);

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  const loadAvailability = useCallback(async () => {
    try {
      const data = await chatService.getAvailability();
      setAvailability(data);
    } catch (error) {
      console.error('Error loading chat availability:', error);
    }
  }, []);

  useEffect(() => {
    loadAvailability();
    const timer = window.setInterval(loadAvailability, 30000);
    return () => window.clearInterval(timer);
  }, [loadAvailability]);

  // Connect websocket
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      websocketService.connect(token);
    }
    return () => { websocketService.disconnect(); };
  }, []);

  useEffect(() => {
    const handler = () => loadThreads();
    websocketService.onMessage(handler);
    return () => websocketService.removeMessageHandler(handler);
  }, [loadThreads]);

  const handleSelectThread = (t: ChatThread) => {
    setSelectedThread(t);
  };

  const handleThreadUpdate = () => {
    loadThreads();
  };

  const handleSearch = () => {
    loadThreads();
  };

  const handleGuestSelect = async (_guest: Guest, recipientType: string) => {
    try {
      // Guest list IDs are police-file records, not checkin_id. Start general hotel→agency thread.
      // (Linking to checkin requires a valid checkins.id — optional future enhancement.)
      const thread = await chatService.createThread(undefined, recipientType);
      setShowGuestPicker(false);
      await loadThreads();
      setSelectedThread(thread);
    } catch (error) {
      console.error('Error creating thread:', error);
      alert('Impossible de créer la conversation. Veuillez réessayer.');
    }
  };

  const handleNewConversation = async (recipientType: string) => {
    try {
      const thread = await chatService.createThread(undefined, recipientType);
      setShowGuestPicker(false);
      await loadThreads();
      setSelectedThread(thread);
    } catch (error) {
      console.error('Error creating thread:', error);
      alert('Impossible de créer la conversation. Veuillez réessayer.');
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-4rem)] bg-gray-100">
      {/* Left: thread list */}
      <div className="w-[360px] flex-shrink-0">
        <ThreadList
          threads={threads}
          loading={loading}
          selectedId={selectedThread?.id}
          availability={availability}
          onSelect={handleSelectThread}
          onNewConversation={() => setShowGuestPicker(true)}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSearch={handleSearch}
        />
      </div>

      {/* Right: conversation */}
      <div className="flex-1">
        {selectedThread ? (
          <ConversationView thread={selectedThread} availability={availability} onThreadUpdate={handleThreadUpdate} />
        ) : (
          <EmptyConversationState availability={availability} onNewConversation={() => setShowGuestPicker(true)} />
        )}
      </div>

      {/* Correspondent picker modal (SCRUM-6: works without guest inventory) */}
      <CorrespondentPickerModal
        open={showGuestPicker}
        onClose={() => setShowGuestPicker(false)}
        onSelectGeneral={handleNewConversation}
        onSelectGuest={handleGuestSelect}
      />
    </div>
  );
}
